/**
 * Tokens de color del Design System — FUENTE DE VERDAD.
 *
 * Valores tal como están definidos en el style guide de Figma (frame "Colors",
 * node 2:82). Se declaran una sola vez acá y quedan disponibles para usar en la app
 * importando `dsColors` (ej. `style={{ background: dsColors.brand[500] }}`).
 *
 * NOTA sobre Tailwind: estos nombres (`brand`, `neutral`, `blue`, …) COLISIONAN con
 * los tokens actuales de la app (`tailwind.config.js` ya define `brand`/`neutral`) y
 * con la paleta default de Tailwind (`blue`/`red`/…). Por eso NO se cablearon todavía
 * como clases globales: hacerlo cambiaría el aspecto de toda la app. Definir el
 * esquema de exposición (namespace vs. migración) es una decisión aparte.
 */

export interface ColorSwatch {
  /** Etiqueta visible (step numérico o nombre), ej. '500' o 'White'. */
  label: string;
  hex: string;
}

export interface ColorGroup {
  name: string;
  description?: string;
  swatches: ColorSwatch[];
}

/** Paleta plana — fuente de verdad de los valores. */
export const dsColors = {
  base: { white: '#FFFFFF', black: '#000000' },
  brand: {
    50: '#F7F8FB',
    100: '#F0F2F7',
    200: '#D6F8FB',
    300: '#60E7F0',
    500: '#1644EB',
    600: '#1238C8',
    700: '#01269B',
    900: '#02062B',
  },
  neutral: {
    50: '#FAFAFA',
    100: '#F4F4F5',
    200: '#E4E4E7',
    300: '#D4D4D8',
    400: '#9F9FA9',
    500: '#71717B',
    600: '#52525C',
    700: '#3F3F46',
    800: '#27272A',
    900: '#18181B',
    950: '#09090B',
  },
  blue: { 50: '#DBEAFE', 500: '#3B82F6', 900: '#1E40AF' },
  red: { 50: '#FEE2E2', 500: '#EF4444', 900: '#991B1B' },
  yellow: { 50: '#FEF3C7', 500: '#F59E0B', 900: '#92400E' },
  green: { 50: '#D1FAE5', 500: '#10B981', 900: '#065F46' },
} as const;

/** Convierte un mapa `{ step: hex }` en la lista de swatches para la tabla. */
const steps = (map: Record<string | number, string>): ColorSwatch[] =>
  Object.entries(map).map(([label, hex]) => ({ label, hex }));

/**
 * Grupos para la página de docs (Foundations › Colors), con nombre y descripción.
 * Textos tal como están en el style guide de Figma.
 */
export const COLOR_GROUPS: ColorGroup[] = [
  {
    name: 'Base',
    swatches: [
      { label: 'White', hex: dsColors.base.white },
      { label: 'Black', hex: dsColors.base.black },
    ],
  },
  {
    name: 'Brand',
    description:
      'Color de marca utilizado para transmitir su personalidad. También puede aplicarse en acciones y elementos interactivos, como enlaces de texto y botones. Esto ayuda a reconocer qué elementos son interactivos y cuáles no.',
    swatches: steps(dsColors.brand),
  },
  {
    name: 'Neutral',
    description:
      'Constituye la base del sistema de color. La mayoría de los elementos de una interfaz —como textos, campos de formulario, fondos y divisores— suelen utilizar tonos de gris.',
    swatches: steps(dsColors.neutral),
  },
  {
    name: 'Blue',
    description: 'Se utiliza para indicar un mensaje neutral.',
    swatches: steps(dsColors.blue),
  },
  {
    name: 'Red',
    description:
      'Se utiliza para indicar un mensaje negativo, como un error o una falla del sistema que requiere atención urgente.',
    swatches: steps(dsColors.red),
  },
  {
    name: 'Yellow',
    description:
      'Se utiliza para advertir que se debe actuar con precaución y que realizar una acción podría implicar riesgos.',
    swatches: steps(dsColors.yellow),
  },
  {
    name: 'Green',
    description:
      'Se utiliza para indicar un mensaje positivo o que una acción se completó según lo esperado.',
    swatches: steps(dsColors.green),
  },
];
